import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import {
	DirectionOfFeeString,
	DirectionOfFeeType,
} from '@models/businessSettings/directionOfFees';
import {
	LandTransferTax,
	MatterPropertyType,
	PropertyType,
} from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';

export const DisplayOptions = [
	{
		name: DirectionOfFeeString[DirectionOfFeeType.TRUST_LEDGER],
		value: DirectionOfFeeType.TRUST_LEDGER,
	},
	{
		name: DirectionOfFeeString[DirectionOfFeeType.SOA],
		value: DirectionOfFeeType.SOA,
	},
	{
		name: DirectionOfFeeString[DirectionOfFeeType.NONE],
		value: DirectionOfFeeType.NONE,
	},
];

export const ShowFeeOptions: RadioGroupItem<
	DirectionOfFeeType.TRUST_LEDGER | DirectionOfFeeType.SOA
>[] = [
	{
		label: DirectionOfFeeString[DirectionOfFeeType.TRUST_LEDGER],
		value: DirectionOfFeeType.TRUST_LEDGER,
	},
	{
		label: DirectionOfFeeString[DirectionOfFeeType.SOA],
		value: DirectionOfFeeType.SOA,
	},
];

@UntilDestroy()
@Component({
	selector: 'app-lttsettings-dialog',
	templateUrl: './lttsettings-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LTTSettingsDialogComponent implements OnInit {
	public ICON = ICON;
	public displayIn: DirectionOfFeeType;
	public directionOfFeeType = DirectionOfFeeType;
	public showAdminFeeIn:
		| DirectionOfFeeType.TRUST_LEDGER
		| DirectionOfFeeType.SOA;
	public showFeeOptions = ShowFeeOptions;
	public displayOptions = [{ items: DisplayOptions }];
	public directionOfFeeString = DirectionOfFeeString;
	public ltt: LandTransferTax;
	public separateEntries: boolean;
	public isDisabled = false;

	@Select(SelectedMatter.landTransferTax) ltt$: Observable<LandTransferTax>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.propertyType) propertyType: MatterPropertyType;

	constructor(
		private dialogRef: MatDialogRef<LTTSettingsDialogComponent>,
		private matterDispatch: MatterDispatch,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.ltt$.pipe(untilDestroyed(this)).subscribe((_ltt) => {
			this.ltt = _ltt;
			this.displayIn =
				_ltt.hideFromTL && !_ltt.displayIn
					? DirectionOfFeeType.NONE
					: _ltt?.displayIn || DirectionOfFeeType.TRUST_LEDGER;
			this.showAdminFeeIn =
				_ltt?.mltt?.directionOfAdminFee || DirectionOfFeeType.TRUST_LEDGER;
			this.separateEntries = !_ltt?.mltt?.applied
				? false
				: _ltt?.separateEntriesOnTrustLedger;
		});

		if (
			this.propertyType.type === PropertyType.MOBILE_HOME &&
			this.propertyType.usage === PropertyUsage.RESIDENTIAL
		) {
			this.isDisabled = true;
		}
	}

	setShowAdminFeeIn(
		result: DirectionOfFeeType.TRUST_LEDGER | DirectionOfFeeType.SOA,
	): void {
		this.showAdminFeeIn = result;
	}

	setDisplayIn(
		result: DropdownResult<DropdownOption<DirectionOfFeeType>>,
	): void {
		this.displayIn = result.result.value;
	}

	save(): void {
		if (this.ltt?.displayIn !== this.displayIn) {
			this.analytics.logEvent(
				AnalyticsEvent.LTT_SETTINGS_MATTER_DISPLAY_DROPDOWN_CHANGE_UPDATE,
				{ displayIn: this.displayIn },
			);
		}

		if (this.ltt?.separateEntriesOnTrustLedger !== this.separateEntries) {
			this.analytics.logEvent(
				AnalyticsEvent.LTT_SETTINGS_MATTER_SINGLE_SEPARATE_ENTRY_CHECKBOX_CHANGE_UPDATE,
				{ separateEntries: this.separateEntries },
			);
		}

		if (this.ltt?.mltt?.directionOfAdminFee !== this.showAdminFeeIn) {
			this.analytics.logEvent(
				AnalyticsEvent.LTT_SETTINGS_MATTER_MLTT_ADMIN_FEE_TOGGLE_CHANGE_UPDATE,
				{ showAdminFeeIn: this.showAdminFeeIn },
			);
		}

		this.matterDispatch.UpdateLtt(this.matterId, {
			displayIn: this.displayIn,
			hideFromTL: this.displayIn === DirectionOfFeeType.NONE,
			separateEntriesOnTrustLedger: this.separateEntries,
			mltt: {
				...this.ltt?.mltt,
				directionOfAdminFee: this.showAdminFeeIn,
			},
		});

		this.close();
	}

	close(): void {
		this.dialogRef.close();
	}
}
